import * as Yup from 'yup'

const requiredString = (label: string) =>
    Yup.string()
        .trim()
        .required()
        .label(label)

const requiredNumber = (label: string) =>
    Yup.number()
        .required()
        .label(label)

const cbaRequiredNumber = (label: string) =>
    Yup.number().when("analysis_mode_selector", {
        is: "cba",
        then: (schema) =>
            schema
                .typeError(`${label} must be a number`)
                .required(`${label} is required`)
                .moreThan(0, `${label} must be greater than 0`),
        otherwise: (schema) => schema.nullable(),
    });

const ceaRequiredNumber = (label: string) =>
    Yup.number().when("analysis_mode_selector", {
        is: "cea",
        then: (schema) =>
            schema
                .typeError(`${label} must be a number`)
                .required(`${label} is required`)
                .moreThan(0, `${label} must be greater than 0`),
        otherwise: (schema) => schema.nullable(),
    });

export const conceptAndViabilityStageSchema = {
    final_feasibility_validation: Yup.object({
        feasibility_status: requiredString('Feasibility Status'),
        key_constraints_summary: Yup.string(),
    }),
    cost_benefit_final_model: Yup.object({}),
    fiscal_sustainability_ifsa: Yup.object({
        contingent_liability_exposure: requiredString('Contingent Liability Exposure'),
        fiscal_affordability_index: requiredNumber('Fiscal Affordability Index').min(0, "Fiscal Affordability Index should be between 0 and 100").max(100, "Fiscal Affordability Index should be between 0 and 100"),
        long_term_fiscal_impact_score: requiredNumber('Long Term Fiscal Impact Score').min(0, "Long Term Fiscal Impact Score should be between 0 and 100").max(100, "Long Term Fiscal Impact Score should be between 0 and 100"),
    }),
    risk_finalisation_engine: Yup.object({}),
    long_term_asset_management: Yup.object({
        asset_lifecycle_cost_estimate: requiredNumber('Asset Lifecycle Cost Estimate'),
        infrastructure_longevity: requiredNumber('Infrastructure Longevity'),
        maintenance_strategy_type: requiredString('Maintenance Strategy Type'),
        climate_resilience_score: Yup.number(),
        expenditure_statement: requiredString("Expenditure Statement"),
        funding_statement: requiredString("Funding Statement"),
        cashflow_statement: requiredString("Cashflow Statement"),
        contingent_liability_statement: requiredString("Contingent Liability Statement"),
    }),
    final_investment_decision_engine_core_output: Yup.object({}),
    esg_final_check: Yup.object({
        gender_responsive_impact_compliance: requiredString('Gender-Responsive Impact Compliance'),
        social_benefit_index: requiredString('Social Benefit Index'),
        environmental_compliance_status: requiredString('Environmental Compliance Status'),
        sustainability_certification_status: requiredString('Sustainability Certification Status'),
        social_impact_index: Yup.number().min(0, 'Min 0').max(100, 'Max 100'),
        overall_eia_summary_and_environmental_authorisation_status: requiredString('Overall EIA Summary & Environmental Authorisation Status'),
        key_environmental_impact_findings: requiredString('Key Environmental Impact Findings'),
        key_social_impact_findings: requiredString('Key Social Impact Findings'),
        gender_impact_findings: requiredString('Gender Impact Findings'),
        mitigation_measures_empr_summary: requiredString('Mitigation Measures EMPR Summary'),
        residual_impacts_acceptability: requiredString('Residual Impacts Acceptability'),
        environmental_authorisation_conditions_compliance_status: requiredString('Environmental Authorisation Conditions Compliance Status'),
    }),
    infrastructure_master_plan: Yup.object({
        master_plan_defination: requiredString('Master Plan Definition'),
        master_plan_documents: Yup.array()
            .min(1, 'At least one master plan document is required')
            .required()
            .label('Master Plan Document'),
    }),
    institutional_and_operational_readiness: Yup.object({
        institutional_and_operational_readiness_assessment: requiredString('Institutional & Operational Readiness Assessment'),
    }),
    supervision_reporting_and_evaluation: Yup.object({
        supervision_framework: requiredString('Supervision Framework'),
        evaluation_framework: requiredString('Evaluation Framework'),
        reporting_framework: requiredString('Reporting Framework'),
        me_framework_kpi_data_source: requiredString('M&E Framework — KPIs & Data Sources'),
        me_responsibility_feedback_loops: requiredString('M&E Responsibility & Feedback Loops'),
        reporting_frequency_format: requiredString('Reporting Frequency Format'),
        evaluation_schedule: requiredString('Evaluation Schedule'),
        incentives_for_successful_delivery: requiredString('Incentives for Successful Delivery'),
        penalties_for_non_performance: requiredString('Panelties for Non-Performance'),
    }),
    project_communication_and_stakeholder_engagement_plan: Yup.object({
        investor_engagement_strategies: requiredString('Investor Engagement Strategies'),
        community_participation: requiredString('Community Participation'),
        government_regulatory_interactions: requiredString('Government Regulatory Interactions'),
        collabration_partner_strategy: requiredString('Collabration Partner Strategy'),
        public_interest_considerations: requiredString('Public Interest Considerations'),
    }),
    digital_technology_leverage: Yup.object({
        digital_technology_leverage: requiredString('Digital Technology Leverage'),
    }),
    economic_and_commercial_viability: Yup.object({
        value_for_money: requiredString('Value for Money'),
        summary_financial_vfm_findings: requiredString('Summary of SG1 & SG2 Financial VfM Findings'),
        benefit_analysis: requiredString('Benefit Analysis'),
        summary_financial_benefit_findings: requiredString('Summary of SG1 & SG2 Financial Benefit Findings'),
        risk_evaluation: requiredString('Risk Evaluation'),
        summary_financial_risk_findings: requiredString('Summary of SG1 & SG2 Risk Findings'),
        economic_multipliers: requiredString('Economic Multipliers'),
        summary_economic_multiplier_financial_findings: requiredString('Summary of SG1 & SG2 Economic Multiplier Financial Findings'),
        economies_of_scale: requiredString('Economies of Scale'),
        summary_economies_of_scale_financial_findings: requiredString('Summary of SG1 & SG2 Economies of Scale Financial Findings'),
        cost_effectiveness_analysis: requiredString('Cost-Effectiveness Analysis'),
        summary_cea_financial_findings: requiredString('Summary of SG1 & SG2 CEA Financial Findings'),
        social_economic_analysis: requiredString('Social & Economic Analysis'),
        summary_social_economic_financial_findings: requiredString('Summary of SG1 & SG2 Social & Economic Financial Findings'),
    }),
    implementation_strategy: Yup.object({
        implementation_strategy: requiredString("Implementation Strategy"),
        asisa_investment_framework_alignment: requiredString(
            "ASISA Investment Framework Alignment"
        ),
        esg_policy_statement_integration_approach: requiredString(
            "ESG Policy Statement & Integration Approach"
        ),
        environmental: requiredString("E — Environmental"),
        social: requiredString("S — Social"),
        governance: requiredString("G — Governance"),
        institutional_investor_funding_mobilisation_strategy: requiredString(
            "Institutional Investor Funding Mobilisation Strategy"
        ),
        blended_finance_structure: requiredString(
            "Blended Finance Structure"
        ),
        institutional_investor_participation_requirements: requiredString(
            "Institutional Investor Participation & Requirements"
        ),
        due_diligence_framework_standards: requiredString(
            "Due Diligence Framework & Standards"
        ),
        impact_reporting_disclosure_commitments: requiredString(
            "Impact Reporting & Disclosure Commitments"
        ),
        impact_measurement_framework: requiredString(
            "Impact Measurement Framework"
        ),
        disclosure_framework: requiredString(
            "Disclosure Framework"
        ),
        stewardship_arrangements_crisa_alignment: requiredString(
            "Stewardship Arrangements & CRISA Alignment"
        ),
        risk_management_framework: requiredString(
            "Risk Management Framework"
        ),
        implementation_plan_phasing_schedule: requiredString(
            "Implementation Plan & Phasing Schedule"
        ),
        monitoring_evaluation: requiredString(
            "Monitoring & Evaluation"
        ),
        exit_strategy_liquidity_provision: requiredString(
            "Exit Strategy & Liquidity Provision"
        ),
    }),
    risk_statement: Yup.object({
        problem_statement: requiredString("Problem Statement & Justification for Investment"),
        risk_context: requiredString("Risk Context & Risk Environment"),
        sensitivity_analysis_overview: requiredString("Sensitivity Analysis Overview & Methodology"),
        sensitivity_analysis_conclusion: requiredString("Sensitivity Analysis Conclusions"),
        overall_risk_statement: requiredString("Overall Risk Statement & Investment Risk Acceptance"),
    }),

    economic_multiplier_and_macroeconomics_benefits: Yup.object({
        // Section 1: GDP Contribution & Economic Growth
        gdp_contribution_estimate: requiredString("GDP Contribution Estimate"),
        regional_economic_growth_effects: requiredString("Regional Economic Growth Effects"),

        // Section 2: Employment Multipliers
        direct_employment: requiredString("Direct Employment"),
        indirect_employment: requiredString("Indirect Employment"),
        induced_employment: requiredString("Induced Employment"),

        // Section 3: Sector Multiplier & Supply Chain Effects
        sector_economic_multiplier: requiredString("Sector Economic Multiplier"),
        supply_chain_local_economic_development: requiredString("Supply Chain & Local Economic Development"),
        productivity_trade_facilitation: requiredString("Productivity & Trade Facilitation"),

        // Section 4: Social Returns & Fiscal Benefits
        poverty_reduction_equity_impact: requiredString("Poverty Reduction & Equity Impact"),
        fiscal_returns_to_government: requiredString("Fiscal Returns to Government"),
        sdg_macroeconomic_policy_alignment: requiredString("SDG & Macroeconomic Policy Alignment"),

        // Section 5: Macroeconomic Risk Factors
        macroeconomic_risks_sensitivity: requiredString("Macroeconomic Risks & Sensitivity"),
    }),
    financial_requirements_and_funding_strategy: Yup.object({
        funding_model: requiredString("Funding Model"),
        revenue_strategy: requiredString("Revenue Strategy"),
    }),
    summary_of_option_considered: Yup.object({
        base_case_description: requiredString("Base Case Description"),
        preferred_option: requiredNumber('Preferred Option'),
        preferred_option_government_objectives_justification: requiredString("Preferred Option Government Objectives Justification"),
        value_for_money_conclusion: requiredString('Value for Money Conclusion'),
        supporting_documentation_reference: requiredString('Supporting Documentation Reference'),
    }),

    documents: Yup.object({}),
}

export type IConceptAndViabilityStageSchema =
    keyof typeof conceptAndViabilityStageSchema